import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import {
  TrainingItem, TrainingCompletion, TrainingType, TrainingStatus, Project,
} from '../../../types';
import {
  getProjectTrainingItems, createTrainingItem, updateTrainingItem, deleteTrainingItem,
  getItemCompletions, createCompletion, deleteCompletion, getCompletionCounts,
} from '../../../services/trainingService';
import PeoplePicker, { Person } from '../../../components/PeoplePicker';
import ModuleGuard from '../../../components/ModuleGuard';
import Badge from '../../../components/Badge';
import FilterPills from '../../../components/FilterPills';
import AutoSizingTextarea from '../../../components/AutoSizingTextarea';
import BoardModal, {
  BMCancelButton, BMPrimaryButton, BMDangerButton,
  BM_LABEL, BM_INPUT, BM_TITLE_INPUT,
} from '../../../components/BoardModal';
import { useCurrentUser } from '../../../hooks/useCurrentUser';

interface Props { pca: IPublicClientApplication; projectId: string; project: Project; }

const TYPE_LABEL: Record<number, string> = {
  [TrainingType.Onboarding]: 'Onboarding',
  [TrainingType.Safety]:     'Safety',
  [TrainingType.Technical]:  'Technical',
  [TrainingType.Compliance]: 'Compliance',
  [TrainingType.Other]:      'Other',
};
const TYPE_BADGE: Record<number, { bg: string; fg: string }> = {
  [TrainingType.Onboarding]: { bg: '#E0E7FF', fg: '#3730A3' },
  [TrainingType.Safety]:     { bg: '#FEE2E2', fg: '#991B1B' },
  [TrainingType.Technical]:  { bg: '#D4EEF5', fg: '#0A5568' },
  [TrainingType.Compliance]: { bg: '#FEF3C7', fg: '#92400E' },
  [TrainingType.Other]:      { bg: '#F3F4F6', fg: '#5A6272' },
};
const STATUS_LABEL: Record<number, string> = {
  [TrainingStatus.NotStarted]: 'Not Started',
  [TrainingStatus.InProgress]: 'In Progress',
  [TrainingStatus.Completed]:  'Completed',
  [TrainingStatus.Overdue]:    'Overdue',
};
const STATUS_BADGE: Record<number, { bg: string; fg: string }> = {
  [TrainingStatus.NotStarted]: { bg: '#F3F4F6', fg: '#5A6272' },
  [TrainingStatus.InProgress]: { bg: '#D4EEF5', fg: '#0A5568' },
  [TrainingStatus.Completed]:  { bg: '#D1FAE5', fg: '#065F46' },
  [TrainingStatus.Overdue]:    { bg: '#FEE2E2', fg: '#991B1B' },
};

type Filter = 'all' | 1 | 2 | 3 | 4;
const FILTER_OPTIONS: { value: Filter; label: string }[] = [
  { value: 'all',                    label: 'All' },
  { value: TrainingStatus.NotStarted, label: 'Not Started' },
  { value: TrainingStatus.InProgress, label: 'In Progress' },
  { value: TrainingStatus.Completed,  label: 'Completed' },
  { value: TrainingStatus.Overdue,    label: 'Overdue' },
];

function isOverdue(item: TrainingItem): boolean {
  if (!item.dagrun_pm_duedate) return false;
  if (item.dagrun_pm_status === TrainingStatus.Completed) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(item.dagrun_pm_duedate);
  return due.getTime() < today.getTime();
}

export default function TrainingTab(props: Props) {
  return (
    <ModuleGuard project={props.project} flag="dagrun_pm_module_training">
      <TrainingTabBody {...props} />
    </ModuleGuard>
  );
}

function TrainingTabBody({ pca, projectId }: Props) {
  const [items, setItems] = useState<TrainingItem[]>([]);
  const [completionCounts, setCompletionCounts] = useState<Map<string, number>>(new Map());
  const [filter, setFilter] = useState<Filter>('all');
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState<TrainingItem | null>(null);

  const reload = useCallback(async () => {
    const [list, counts] = await Promise.all([
      getProjectTrainingItems(pca, projectId),
      getCompletionCounts(pca, projectId),
    ]);
    setItems(list);
    setCompletionCounts(counts);
  }, [pca, projectId]);

  useEffect(() => { reload(); }, [reload]);

  const filtered = useMemo(() => items.filter(i => {
    if (filter === 'all') return true;
    if (filter === TrainingStatus.Overdue) return isOverdue(i);
    return i.dagrun_pm_status === filter;
  }), [items, filter]);

  return (
    <div style={{ padding: '20px 32px', fontFamily: 'var(--font-ui)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#0F1117' }}>Training Records</h3>
        <button type="button" onClick={() => setShowNew(true)}
          style={{ background: '#0E7490', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 6, fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          onMouseEnter={e => (e.currentTarget.style.background = '#0A5568')}
          onMouseLeave={e => (e.currentTarget.style.background = '#0E7490')}
        >+ Add Training Item</button>
      </div>

      <div style={{ marginBottom: 14 }}>
        <FilterPills value={filter} options={FILTER_OPTIONS} onChange={setFilter} />
      </div>

      {filtered.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: '#9198A8', fontSize: 14 }}>
          No training items {filter === 'all' ? 'yet' : 'match this filter'}.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ borderBottom: '1px solid #DDE0E6', textAlign: 'left' }}>
            <Th>Training Item</Th><Th>Type</Th><Th>Assigned To</Th><Th>Due Date</Th><Th>Status</Th><Th>Completions</Th>
          </tr></thead>
          <tbody>
            {filtered.map(item => {
              const overdue = isOverdue(item);
              const count = completionCounts.get(item.dagrun_pm_trainingitemid) ?? 0;
              return (
                <tr key={item.dagrun_pm_trainingitemid} onClick={() => setSelected(item)}
                  style={{
                    borderBottom: '0.5px solid #EAECF0', cursor: 'pointer',
                    transition: 'background 0.12s',
                    borderLeft: overdue ? '3px solid #D97706' : '3px solid transparent',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#F5F6F8')}
                  onMouseLeave={e => (e.currentTarget.style.background = '')}>
                  <Td><span style={{ color: '#0F1117', fontSize: 14 }}>{item.dagrun_pm_name}</span></Td>
                  <Td>{item.dagrun_pm_trainingtype != null
                    ? <Badge {...TYPE_BADGE[item.dagrun_pm_trainingtype]}>{TYPE_LABEL[item.dagrun_pm_trainingtype]}</Badge>
                    : <Em />}</Td>
                  <Td><span style={{ fontSize: 13, color: '#5A6272' }}>{item.dagrun_pm_assigneename ?? <Em />}</span></Td>
                  <Td><span style={{ fontSize: 12, color: overdue ? '#991B1B' : '#5A6272', fontFamily: 'var(--font-mono)', fontWeight: overdue ? 600 : 400 }}>
                    {item.dagrun_pm_duedate ? new Date(item.dagrun_pm_duedate).toLocaleDateString() : '—'}
                  </span></Td>
                  <Td>{item.dagrun_pm_status != null
                    ? <Badge {...STATUS_BADGE[overdue ? TrainingStatus.Overdue : item.dagrun_pm_status]}>
                        {STATUS_LABEL[overdue ? TrainingStatus.Overdue : item.dagrun_pm_status]}
                      </Badge>
                    : <Em />}</Td>
                  <Td><span style={{ fontSize: 12, color: '#5A6272', fontFamily: 'var(--font-mono)' }}>
                    {count} logged
                  </span></Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {showNew && (
        <CreateTrainingModal pca={pca} projectId={projectId}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); reload(); }}
        />
      )}
      {selected && (
        <TrainingDrawer pca={pca} item={selected}
          onClose={() => { setSelected(null); reload(); }}
          onDeleted={() => { setSelected(null); reload(); }}
        />
      )}
    </div>
  );
}

const Th = ({ children }: { children: React.ReactNode }) => (
  <th style={{ padding: '8px 12px', fontWeight: 500, fontSize: 12, color: '#5A6272', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{children}</th>
);
const Td = ({ children }: { children: React.ReactNode }) => (
  <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>{children}</td>
);
const Em = () => <span style={{ color: '#B8BEC9', fontSize: 13 }}>—</span>;

function CreateTrainingModal({ pca, projectId, onClose, onCreated }: {
  pca: IPublicClientApplication; projectId: string; onClose: () => void; onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [trainingtype, setTrainingtype] = useState<number>(TrainingType.Onboarding);
  const [assignee, setAssignee] = useState<Person | null>(null);
  const [duedate, setDuedate] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim() || saving) return;
    setSaving(true); setError(null);
    try {
      await createTrainingItem(pca, {
        projectId,
        name: name.trim(),
        trainingtype,
        assigneeid: assignee?.aadObjectId ?? null,
        assigneename: assignee?.displayName ?? null,
        duedate: duedate || null,
        notes: notes.trim() || null,
      });
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally { setSaving(false); }
  };

  return (
    <BoardModal open title="Add Training Item" onClose={onClose}
      footer={<>
        <BMCancelButton onClick={onClose} disabled={saving} />
        <BMPrimaryButton onClick={submit} disabled={saving || !name.trim()}>
          {saving ? 'Creating…' : 'Add Training Item'}
        </BMPrimaryButton>
      </>}>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Training Item *</label>
        <input autoFocus value={name} onChange={e => setName(e.target.value)}
          style={BM_TITLE_INPUT} placeholder="e.g. OSHA 10-Hour, HIPAA Annual" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={BM_LABEL}>Type</label>
          <select value={trainingtype} onChange={e => setTrainingtype(Number(e.target.value))} style={BM_INPUT}>
            {[TrainingType.Onboarding, TrainingType.Safety, TrainingType.Technical,
              TrainingType.Compliance, TrainingType.Other].map(v =>
              <option key={v} value={v}>{TYPE_LABEL[v]}</option>)}
          </select>
        </div>
        <div>
          <label style={BM_LABEL}>Due Date</label>
          <input type="date" value={duedate} onChange={e => setDuedate(e.target.value)} style={BM_INPUT} />
        </div>
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Assigned To</label>
        <PeoplePicker pca={pca} value={assignee} onChange={setAssignee} placeholder="Assign to…" />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Notes</label>
        <AutoSizingTextarea value={notes} onChange={setNotes} />
      </div>
      {error && <div style={{ marginTop: 12, color: '#DC2626', fontSize: 12 }}>{error}</div>}
    </BoardModal>
  );
}

function TrainingDrawer({ pca, item, onClose, onDeleted }: {
  pca: IPublicClientApplication; item: TrainingItem; onClose: () => void; onDeleted: () => void;
}) {
  const [draft, setDraft] = useState<TrainingItem>(item);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const flashTimer = useRef<number | null>(null);
  const textDebounce = useRef<number | null>(null);

  useEffect(() => { setDraft(item); }, [item]);

  const patch = async (fields: Partial<TrainingItem>) => {
    setSaving(true);
    try {
      await updateTrainingItem(pca, draft.dagrun_pm_trainingitemid, fields as Record<string, unknown>);
      setSavedFlash(true);
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
      flashTimer.current = window.setTimeout(() => setSavedFlash(false), 1500);
    } finally { setSaving(false); }
  };
  const setAndPatch = <K extends keyof TrainingItem>(k: K, v: TrainingItem[K]) => {
    setDraft(d => ({ ...d, [k]: v })); patch({ [k]: v } as Partial<TrainingItem>);
  };
  const setText = <K extends keyof TrainingItem>(k: K, v: TrainingItem[K]) => {
    setDraft(d => ({ ...d, [k]: v }));
    if (textDebounce.current) window.clearTimeout(textDebounce.current);
    textDebounce.current = window.setTimeout(() => patch({ [k]: v } as Partial<TrainingItem>), 600);
  };
  const flush = <K extends keyof TrainingItem>(k: K, v: TrainingItem[K]) => {
    if (textDebounce.current) window.clearTimeout(textDebounce.current);
    patch({ [k]: v } as Partial<TrainingItem>);
  };
  const doDelete = async () => {
    setSaving(true);
    try { await deleteTrainingItem(pca, draft.dagrun_pm_trainingitemid); onDeleted(); } finally { setSaving(false); }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,17,23,0.18)', zIndex: 100 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, height: '100%', width: 520,
        background: '#fff', borderLeft: '0.5px solid #EAECF0',
        boxShadow: '0 4px 24px rgba(15,17,23,0.12)', zIndex: 101,
        overflowY: 'auto', padding: '16px 24px 32px 24px',
        fontFamily: 'var(--font-ui)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 11, color: '#9198A8', opacity: savedFlash ? 1 : 0, transition: 'opacity 0.2s' }}>
            {savedFlash ? 'Saved ✓' : ''}
          </span>
          <button onClick={onClose} aria-label="Close" style={{ width: 28, height: 28, border: 'none', background: 'transparent', cursor: 'pointer', color: '#5A6272', fontSize: 18 }}>×</button>
        </div>

        <input value={draft.dagrun_pm_name}
          onChange={e => setText('dagrun_pm_name', e.target.value)}
          onBlur={e => flush('dagrun_pm_name', e.target.value)}
          style={{ fontSize: 18, fontWeight: 600, color: '#0F1117', border: 'none', padding: 0, marginBottom: 12, width: '100%', background: 'transparent' }} />

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
          <select value={draft.dagrun_pm_status ?? TrainingStatus.NotStarted}
            onChange={e => setAndPatch('dagrun_pm_status', Number(e.target.value))}
            style={{ ...BM_INPUT, width: 'auto', padding: '4px 8px', fontSize: 12 }}>
            {[TrainingStatus.NotStarted, TrainingStatus.InProgress, TrainingStatus.Completed].map(v =>
              <option key={v} value={v}>{STATUS_LABEL[v]}</option>)}
          </select>
          <select value={draft.dagrun_pm_trainingtype ?? TrainingType.Onboarding}
            onChange={e => setAndPatch('dagrun_pm_trainingtype', Number(e.target.value))}
            style={{ ...BM_INPUT, width: 'auto', padding: '4px 8px', fontSize: 12 }}>
            {[TrainingType.Onboarding, TrainingType.Safety, TrainingType.Technical,
              TrainingType.Compliance, TrainingType.Other].map(v =>
              <option key={v} value={v}>{TYPE_LABEL[v]}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={BM_LABEL}>Due Date</label>
          <input type="date" value={draft.dagrun_pm_duedate?.slice(0, 10) ?? ''}
            onChange={e => setAndPatch('dagrun_pm_duedate', e.target.value || null)} style={BM_INPUT} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={BM_LABEL}>Assigned To</label>
          <PeoplePicker pca={pca}
            value={draft.dagrun_pm_assigneeid ? { aadObjectId: draft.dagrun_pm_assigneeid, displayName: draft.dagrun_pm_assigneename ?? draft.dagrun_pm_assigneeid } : null}
            onChange={p => {
              const fields = {
                dagrun_pm_assigneeid: p?.aadObjectId ?? null,
                dagrun_pm_assigneename: p?.displayName ?? null,
              };
              setDraft(d => ({ ...d, ...fields }));
              patch(fields);
            }}
            placeholder="Assign to…"
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={BM_LABEL}>Notes</label>
          <AutoSizingTextarea
            value={draft.dagrun_pm_notes ?? ''}
            onChange={v => setText('dagrun_pm_notes', v || null)}
            onBlur={v => flush('dagrun_pm_notes', v || null)}
          />
        </div>

        <CompletionLogSection pca={pca} trainingItemId={draft.dagrun_pm_trainingitemid} />

        <div style={{ marginTop: 28, display: 'flex', justifyContent: 'flex-end' }}>
          <BMDangerButton onClick={() => setConfirmDelete(true)}>Delete</BMDangerButton>
        </div>
      </div>

      {confirmDelete && (
        <BoardModal open title="Delete Training Item" onClose={() => setConfirmDelete(false)}
          footer={<>
            <BMCancelButton onClick={() => setConfirmDelete(false)} disabled={saving} />
            <BMDangerButton onClick={doDelete} disabled={saving}>{saving ? 'Deleting…' : 'Delete'}</BMDangerButton>
          </>}>
          Delete <strong style={{ color: '#0F1117' }}>{draft.dagrun_pm_name}</strong> and all its completion records?
        </BoardModal>
      )}
    </>
  );
}

function CompletionLogSection({ pca, trainingItemId }: { pca: IPublicClientApplication; trainingItemId: string }) {
  const me = useCurrentUser(pca, '');
  const [completions, setCompletions] = useState<TrainingCompletion[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [compBy, setCompBy] = useState<Person | null>(null);
  const [compDate, setCompDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [score, setScore] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      setCompletions(await getItemCompletions(pca, trainingItemId));
    } finally { setLoading(false); }
  }, [pca, trainingItemId]);
  useEffect(() => { reload(); }, [reload]);

  // Default the completed-by picker to the current user once me is loaded.
  useEffect(() => {
    if (me && !compBy) setCompBy({ aadObjectId: me.oid, displayName: me.displayName });
  }, [me, compBy]);

  const startAdd = () => { setAdding(true); };
  const cancelAdd = () => {
    setAdding(false);
    setCompDate(new Date().toISOString().slice(0, 10));
    setScore(''); setNotes('');
  };

  const save = async () => {
    if (!compBy || !compDate || saving) return;
    setSaving(true);
    try {
      await createCompletion(pca, {
        trainingItemId,
        completedon: new Date(compDate).toISOString(),
        completedbyid: compBy.aadObjectId,
        completedbyname: compBy.displayName,
        score: score === '' ? null : Number(score),
        notes: notes.trim() || null,
      });
      cancelAdd();
      reload();
    } finally { setSaving(false); }
  };

  const remove = async (c: TrainingCompletion) => {
    await deleteCompletion(pca, c.dagrun_pm_trainingcompletionid);
    reload();
  };

  return (
    <div style={{ marginTop: 24, borderTop: '0.5px solid #EAECF0', paddingTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#5A6272', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
          Completion Log {completions.length > 0 && <span style={{ color: '#9198A8', fontWeight: 400 }}>({completions.length})</span>}
        </div>
        {!adding && (
          <button type="button" onClick={startAdd}
            style={{ background: 'transparent', color: '#0E7490', border: '1px solid #0E7490', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, fontFamily: 'var(--font-ui)', cursor: 'pointer' }}
          >+ Log Completion</button>
        )}
      </div>

      {loading ? (
        <div style={{ fontSize: 12, color: '#9198A8' }}>Loading…</div>
      ) : (
        <>
          {completions.length === 0 && !adding && (
            <div style={{ fontSize: 12, color: '#9198A8', fontStyle: 'italic', marginBottom: 10 }}>No completions logged yet.</div>
          )}
          {completions.map(c => (
            <div key={c.dagrun_pm_trainingcompletionid} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 0', borderBottom: '0.5px solid #EAECF0', fontSize: 13,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: '#0F1117', fontWeight: 500 }}>{c.dagrun_pm_completedbyname ?? '—'}</div>
                {c.dagrun_pm_notes && (
                  <div style={{ fontSize: 12, color: '#5A6272', marginTop: 2, whiteSpace: 'pre-wrap' }}>{c.dagrun_pm_notes}</div>
                )}
              </div>
              {c.dagrun_pm_score != null && (
                <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: '#5A6272', background: '#F3F4F6', padding: '2px 6px', borderRadius: 3 }}>
                  {c.dagrun_pm_score}
                </span>
              )}
              <span style={{ fontSize: 11, color: '#9198A8', fontFamily: 'var(--font-mono)' }}>
                {c.dagrun_pm_completedon ? new Date(c.dagrun_pm_completedon).toLocaleDateString() : '—'}
              </span>
              <button type="button" onClick={() => remove(c)} aria-label="Delete"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#9198A8', fontSize: 14 }}
                onMouseEnter={e => (e.currentTarget.style.color = '#DC2626')}
                onMouseLeave={e => (e.currentTarget.style.color = '#9198A8')}
              >×</button>
            </div>
          ))}

          {adding && (
            <div style={{ marginTop: 10, padding: 10, background: '#F5F6F8', borderRadius: 6 }}>
              <div style={{ marginBottom: 8 }}>
                <label style={BM_LABEL}>Completed By</label>
                <PeoplePicker pca={pca} value={compBy} onChange={setCompBy} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <div>
                  <label style={BM_LABEL}>Date</label>
                  <input type="date" value={compDate} onChange={e => setCompDate(e.target.value)} style={BM_INPUT} />
                </div>
                <div>
                  <label style={BM_LABEL}>Score (optional)</label>
                  <input type="number" min={0} max={100} value={score}
                    onChange={e => setScore(e.target.value)} style={BM_INPUT} />
                </div>
              </div>
              <div style={{ marginBottom: 8 }}>
                <label style={BM_LABEL}>Notes (optional)</label>
                <AutoSizingTextarea value={notes} onChange={setNotes} minHeight={48} rows={2} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                <button type="button" onClick={cancelAdd} disabled={saving}
                  style={{ background: 'transparent', border: '1px solid #DDE0E6', color: '#5A6272', padding: '4px 10px', fontSize: 12, borderRadius: 4, cursor: 'pointer' }}
                >Cancel</button>
                <button type="button" onClick={save} disabled={saving || !compBy || !compDate}
                  style={{ background: '#0E7490', color: '#fff', border: 'none', padding: '4px 10px', fontSize: 12, fontWeight: 600, borderRadius: 4, cursor: saving || !compBy ? 'not-allowed' : 'pointer', opacity: saving || !compBy ? 0.6 : 1 }}
                >{saving ? 'Saving…' : 'Log Completion'}</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
